import { useCallback, useEffect, useState } from "react";
import useCrmApi from "../../../components/hook/crm-req";

// Data warehouse profiles integration — see
// docs/data-warehouse-traits-integration.md. Additive alongside the existing
// mock /traits/customers (../api.ts), which stays the default Profile
// Explorer experience. This is read-only, opt-in "Live Data" content, scoped
// server-side to the caller's tenant. Per the doc's migration guidance:
//   - identifier lookup only fires on an explicit search submit, never a
//     partial identifier or client-side filter;
//   - identity graph / events / merge history are only requested after the
//     profile detail request itself has succeeded;
//   - an empty result for a real tenant is valid and expected right now —
//     most warehouse profiles have no tenant_id yet and are correctly
//     excluded, so this should never silently fall back to unscoped data.

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

export interface WarehouseProfileSummary {
  profile_id: string;
  first_name: string | null;
  last_name: string | null;
  gender: string | null;
  age_group: string | null;
  location_state: string | null;
  location_city: string | null;
  location_lga: string | null;
  country: string | null;
  interest: string | null;
  income_band: string | null;
  credit_score: number | null;
  updated_at: string;
  trait_count: number;
  identifier_types: string[];
}

export interface WarehouseProfileListResponse {
  data: WarehouseProfileSummary[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  source: "data_warehouse";
}

export interface WarehouseIdentifier {
  id_type: string;
  masked_value: string;
  is_primary: boolean;
  confidence: number;
  source: string;
}

export interface WarehouseConsent {
  scope: string;
  granted: boolean;
  recorded_at: string;
  source: string;
}

export interface WarehouseProfileDetail extends WarehouseProfileSummary {
  created_at: string;
  trait_provenance: unknown;
}

// A single computed trait on a profile — confirmed live shape (was rendered
// as raw JSON before; see the "traits" tab in index.tsx for the real UI).
export interface WarehouseTrait {
  trait_name: string;
  trait_value: unknown;
  computed_at: string;
  model_version?: string;
  trait_type?: string;
  data_type?: string;
  source?: string;
  is_synthetic?: boolean;
}

export interface WarehouseProfileDetailResponse {
  profile: WarehouseProfileDetail;
  traits: WarehouseTrait[];
  identifiers: WarehouseIdentifier[];
  consents: WarehouseConsent[];
  source: "data_warehouse";
}

export interface WarehouseLookupResponse {
  profile_id: string;
  matched_identifier: { id_type: string; masked_value: string };
  identifier_types: string[];
  source: "data_warehouse";
}

export interface WarehouseIdentityEdge {
  edge_id: string;
  left: { id_type: string; masked_value: string };
  right: { id_type: string; masked_value: string };
  source_table: string;
  observed_at: string;
  confidence: number;
  edge_strength: string;
}

export interface WarehouseIdentityGraphResponse {
  profile_id: string;
  data: WarehouseIdentityEdge[];
  total: number;
  source: "data_warehouse";
}

export interface WarehouseEvent {
  source_table: string;
  event_id: string;
  event_name: string;
  event_category: string;
  occurred_at: string;
  ingested_at: string;
  environment: string;
  source: string;
  properties: Record<string, unknown>;
}

export interface WarehouseEventsResponse {
  profile_id: string;
  data: WarehouseEvent[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  source: "data_warehouse";
}

export interface WarehouseMerge {
  merge_id: string;
  confidence: number;
  algorithm_version: string;
  member_count: number;
  created_at: string;
  reversed_at: string | null;
  reversed_reason: string | null;
}

export interface WarehouseMergeHistoryResponse {
  profile_id: string;
  data: WarehouseMerge[];
  total: number;
  source: "data_warehouse";
}

// ─── Profile list — pagination + filters ───────────────────────────────────
// `search`/`gender`/`country`/`incomeBand` are sent under their own field
// name (same convention already confirmed on /traits/warehouse/catalog's
// `search`/`category` params, and these are the exact keys the profile
// object itself returns) — not yet confirmed against a shared doc for this
// specific endpoint, so flag/adjust if the backend expects different names.
export interface WarehouseProfileFilters {
  page: number;
  perPage: number;
  search?: string;
  gender?: string;
  country?: string;
  incomeBand?: string;
}

export function useWarehouseProfiles(filters: WarehouseProfileFilters | null) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseProfileListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!filters) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const params: Record<string, string | number> = { page: filters.page, per_page: filters.perPage };
      if (filters.search?.trim()) params.search = filters.search.trim();
      if (filters.gender) params.gender = filters.gender;
      if (filters.country) params.country = filters.country;
      if (filters.incomeBand) params.income_band = filters.incomeBand;
      const [json, status] = await makeRequest(undefined, params);
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(json);
      else setError("Couldn't load warehouse profiles.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.page, filters?.perPage, filters?.search, filters?.gender, filters?.country, filters?.incomeBand, reloadKey]);

  return { data, loading, error, retry: () => setReloadKey((k) => k + 1) };
}

// ─── Identifier lookup — explicit submit only ──────────────────────────────
export function useWarehouseLookup() {
  const { loading, makeRequest } = useCrmApi("/traits/warehouse/profiles/lookup", "GET", authHeaders());

  const lookup = useCallback(
    async (identifier: string, identifierType: string): Promise<{ result: WarehouseLookupResponse | null; notFound: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, { identifier, identifier_type: identifierType });
      if (status === 200) return { result: json, notFound: false, error: null };
      if (status === 404) return { result: null, notFound: true, error: null };
      return { result: null, notFound: false, error: "Couldn't look up that identifier." };
    },
    [makeRequest]
  );

  return { loading, lookup };
}

// ─── Profile detail ─────────────────────────────────────────────────────────
export function useWarehouseProfileDetail(profileId: string | null) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseProfileDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) {
      setData(null);
      setNotFound(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, profileId);
      if (cancelled) return;
      setLoading(false);
      if (status === 200) setData(json);
      else if (status === 404) setNotFound(true);
      else setError("Couldn't load this profile.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  return { data, loading, notFound, error };
}

// ─── Identity graph / events / merge history — only once `ready` (i.e. the
// profile detail request above has already succeeded) ─────────────────────
export function useWarehouseIdentityGraph(profileId: string | null, ready: boolean) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseIdentityGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profileId || !ready) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, `${profileId}/identity-graph`);
      if (cancelled) return;
      setLoading(false);
      setData(status >= 200 && status < 300 ? json : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, ready]);

  return { data, loading };
}

export function useWarehouseEvents(profileId: string | null, ready: boolean, page: number, perPage: number) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseEventsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profileId || !ready) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, { page, per_page: perPage }, `${profileId}/events`);
      if (cancelled) return;
      setLoading(false);
      setData(status >= 200 && status < 300 ? json : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, ready, page, perPage]);

  return { data, loading };
}

export function useWarehouseMergeHistory(profileId: string | null, ready: boolean) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseMergeHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profileId || !ready) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, `${profileId}/merge-history`);
      if (cancelled) return;
      setLoading(false);
      setData(status >= 200 && status < 300 ? json : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, ready]);

  return { data, loading };
}

// ─── Sample profiles for the trait builder preview (mirrors ../traits/
// api.ts's useSampleProfiles, warehouse-backed variant) ────────────────────
export function useWarehouseSampleProfiles(enabled: boolean, count = 3) {
  const { makeRequest } = useCrmApi("/traits/warehouse/profiles", "GET", authHeaders());
  const [data, setData] = useState<WarehouseProfileSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, { page: 1, per_page: count });
      if (cancelled) return;
      setLoading(false);
      setData(status >= 200 && status < 300 && Array.isArray(json?.data) ? json.data : []);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, count]);

  return { data, loading };
}

export function formatWarehouseTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// First/last name now come back on the profile (previously always null) —
// shows the real name when at least one half is set, falling back to the
// profile id only when neither is (per direct request: don't show a raw id
// list when a name is available).
export function warehouseProfileName(p: { first_name: string | null; last_name: string | null; profile_id: string }): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || p.profile_id;
}
